import React, { useState, useEffect } from 'react';
import { Check, Search, Users, MessageCircle, X, BookMarked, Video, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useChats } from '@/hooks/useChats';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

interface Follower {
  id: string;
  username: string;
  name?: string;
  image?: string;
}

interface FirstBook {
  id: string;
  title: string;
  authors: string[];
  cover_url?: string;
}

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareData?: {
    kind: 'book' | 'review';
    book_id?: string;
    review_id?: string;
    book?: {
      id: string;
      title: string;
      authors: string[];
      cover_url?: string;
    };
  };
  mode?: 'share' | 'new-chat' | 'new-book-club';
}

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  shareData,
  mode = 'share'
}) => {
  const { user } = useAuth();
  const { chats, createDMChat, createGroupChat } = useChats();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [followers, setFollowers] = useState<Follower[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedChat, setSelectedChat] = useState<string>('');
  const [groupTitle, setGroupTitle] = useState('');
  const [isGroup, setIsGroup] = useState(mode === 'new-book-club');
  const [isBookClub, setIsBookClub] = useState(mode === 'new-book-club');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('direct');
  const [clubDescription, setClubDescription] = useState('');
  const [firstBook, setFirstBook] = useState<FirstBook | null>(null);
  const [bookSearchQuery, setBookSearchQuery] = useState('');
  const [bookSearchResults, setBookSearchResults] = useState<FirstBook[]>([]);
  const [bookSearching, setBookSearching] = useState(false);
  const [meetingFormat, setMeetingFormat] = useState<'virtual' | 'in_person' | 'hybrid'>('virtual');
  const [meetingFrequency, setMeetingFrequency] = useState<string>('monthly');
  const [nextMeetingDate, setNextMeetingDate] = useState<Date | undefined>();
  const [meetingLocation, setMeetingLocation] = useState('');
  const [readingPace, setReadingPace] = useState<string>('');
  const [genreFocus, setGenreFocus] = useState<string[]>([]);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      fetchFollowers();
      if (mode === 'new-book-club') {
        setIsGroup(true);
        setIsBookClub(true);
        setActiveTab('direct');
      }
    }
  }, [isOpen, user, mode]);

  const fetchFollowers = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id)
        .eq('status', 'accepted');

      if (error) throw error;

      if (data && data.length > 0) {
        const userIds = data.map(follow => follow.following_id);
        
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, username, name, image')
          .in('id', userIds);
          
        if (profilesError) throw profilesError;
        
        setFollowers(profilesData || []);
      }
    } catch (error) {
      console.error('Error fetching followers:', error);
    }
  };

  const searchBooks = async (query: string) => {
    if (query.length < 2) {
      setBookSearchResults([]);
      return;
    }
    setBookSearching(true);
    try {
      const { data, error } = await supabase
        .from('books')
        .select('id, title, authors, cover_url')
        .ilike('title', `%${query}%`)
        .limit(5);
      if (error) throw error;
      setBookSearchResults(
        (data || []).map(b => ({
          id: b.id,
          title: b.title,
          authors: b.authors || [],
          cover_url: b.cover_url || undefined,
        }))
      );
    } catch {
      setBookSearchResults([]);
    } finally {
      setBookSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (bookSearchQuery.trim()) {
        searchBooks(bookSearchQuery.trim());
      } else {
        setBookSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [bookSearchQuery]);

  const getOtherUserName = (chat: any) => {
    const otherMember = chat.members?.find((m: any) => m.user_id !== user?.id);
    return otherMember?.user?.name || otherMember?.user?.username;
  };

  const filteredFollowers = followers.filter(follower =>
    follower.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    follower.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredChats = chats.filter(chat => {
    const title = chat.is_group ? chat.title : getOtherUserName(chat);
    return title?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleUserToggle = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleShare = async () => {
    if (!user) return;

    setLoading(true);
    try {
      let chatId: string;

      if (activeTab === 'existing') {
        chatId = selectedChat;
      } else {
        // Create new chat
        if (isGroup || selectedUsers.length > 1) {
          const title = groupTitle.trim() || 'New Group';
          chatId = await createGroupChat(title, selectedUsers, isBookClub);
        } else if (selectedUsers.length === 1) {
          chatId = await createDMChat(selectedUsers[0]);
        } else {
          throw new Error('Please select at least one person');
        }
      }

      // Send club description as first message if provided
      if (mode === 'new-book-club' && clubDescription.trim()) {
        await supabase.from('messages').insert([{
          chat_id: chatId,
          sender_id: user.id,
          type: 'text',
          text: `📖 About this club: ${clubDescription.trim()}`,
        }]);
      }

      // Add first book to suggestions if selected
      if (mode === 'new-book-club' && firstBook) {
        try {
          await supabase.from('chat_book_suggestions').insert({
            chat_id: chatId,
            book_id: firstBook.id,
            suggested_by: user.id,
            status: 'reading',
          });
        } catch (err: any) {
          if (err.code !== '23505') console.error('Error adding first book:', err);
        }
      }

      // Insert book club settings
      if (mode === 'new-book-club') {
        try {
          await supabase.from('book_club_settings').insert({
            chat_id: chatId,
            meeting_format: meetingFormat,
            meeting_frequency: meetingFrequency,
            next_meeting_at: nextMeetingDate?.toISOString() || null,
            meeting_location: meetingLocation.trim() || null,
            reading_pace: readingPace || null,
            genre_focus: genreFocus,
          });
        } catch (err) {
          console.error('Error creating book club settings:', err);
        }
      }

      // Send share message if sharing content
      if (shareData && mode === 'share') {
        await supabase.from('messages').insert([{
          chat_id: chatId,
          sender_id: user.id,
          type: 'share',
          share_payload: shareData,
        }]);

        // Create share record
        await supabase.from('shares').insert([{
          chat_id: chatId,
          sender_id: user.id,
          kind: shareData.kind,
          book_id: shareData.book_id,
          review_id: shareData.review_id,
        }]);

        // Check if this is a book club chat and add to suggestions
        const { data: chatData } = await supabase
          .from('chats')
          .select('is_book_club')
          .eq('id', chatId)
          .single();
        
        if (chatData?.is_book_club && shareData.kind === 'book' && shareData.book_id) {
          try {
            await supabase.from('chat_book_suggestions').insert({
              chat_id: chatId,
              book_id: shareData.book_id,
              suggested_by: user.id,
              status: 'suggested'
            });
          } catch (err: any) {
            // Ignore duplicate errors
            if (err.code !== '23505') console.error('Error adding to suggestions:', err);
          }
        }

        toast({
          title: 'Shared successfully!',
          description: `${shareData.kind} shared to chat`,
        });
      }

      navigate(`/chat/${chatId}`);
      onClose();
    } catch (error) {
      console.error('Error sharing:', error);
      toast({
        title: 'Error',
        description: 'Failed to share content',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const canShare = () => {
    if (activeTab === 'existing') {
      return selectedChat;
    }
    if (mode === 'new-book-club') {
      return selectedUsers.length > 0 && groupTitle.trim().length > 0;
    }
    return selectedUsers.length > 0;
  };

  const isBookClubMode = mode === 'new-book-club';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isBookClubMode ? 'New Book Club' : mode === 'share' ? 'Share' : 'New Chat'}
            {shareData && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                {shareData.book?.title}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={isBookClubMode ? 'direct' : activeTab} onValueChange={setActiveTab}>
          {!isBookClubMode && (
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="direct">Direct</TabsTrigger>
              <TabsTrigger value="existing">Existing Chat</TabsTrigger>
            </TabsList>
          )}

          <TabsContent value="direct" className="space-y-4">
            {/* Book Club Hero Section */}
            {isBookClubMode && (
              <div className="text-center py-4 space-y-1.5 border-b border-border mb-2">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <BookMarked className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Start a Book Club</h3>
                <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                  Create a space where members can suggest books, vote on what to read next, and discuss together.
                </p>
              </div>
            )}

            {/* Club Name */}
            {isBookClubMode && (
              <Input
                placeholder="Book club name *"
                value={groupTitle}
                onChange={(e) => setGroupTitle(e.target.value)}
              />
            )}

            {/* Club Description */}
            {isBookClubMode && (
              <Textarea
                placeholder="What's this club about? (optional)"
                value={clubDescription}
                onChange={(e) => setClubDescription(e.target.value)}
                className="min-h-[60px] resize-none"
                rows={2}
              />
            )}

            {/* First Book Picker */}
            {isBookClubMode && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">First book (optional)</Label>
                {firstBook ? (
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                    {firstBook.cover_url && (
                      <img
                        src={firstBook.cover_url}
                        alt={firstBook.title}
                        className="w-10 h-14 rounded object-cover"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{firstBook.title}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {firstBook.authors.join(', ')}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFirstBook(null)}
                      className="shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search for a book..."
                        value={bookSearchQuery}
                        onChange={(e) => setBookSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    {bookSearchResults.length > 0 && (
                      <div className="border border-border rounded-lg overflow-hidden">
                        {bookSearchResults.map((book) => (
                          <div
                            key={book.id}
                            className="flex items-center gap-3 p-2 hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => {
                              setFirstBook(book);
                              setBookSearchQuery('');
                              setBookSearchResults([]);
                            }}
                          >
                            {book.cover_url && (
                              <img
                                src={book.cover_url}
                                alt={book.title}
                                className="w-8 h-11 rounded object-cover"
                              />
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{book.title}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {book.authors.join(', ')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {bookSearching && (
                      <p className="text-xs text-muted-foreground px-1">Searching...</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Club Details (collapsible) */}
            {isBookClubMode && (
              <Collapsible open={showDetails} onOpenChange={setShowDetails}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full justify-between text-muted-foreground">
                    <span className="text-sm">Meeting & Reading Details</span>
                    {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-2">
                  {/* Meeting Format */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Format</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {([
                        { value: 'virtual', label: 'Virtual', icon: Video },
                        { value: 'in_person', label: 'In Person', icon: MapPin },
                        { value: 'hybrid', label: 'Hybrid', icon: Users },
                      ] as const).map(({ value, label, icon: Icon }) => (
                        <Button
                          key={value}
                          type="button"
                          variant={meetingFormat === value ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setMeetingFormat(value)}
                          className="flex items-center gap-1.5"
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Meeting Frequency */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">How Often</Label>
                    <Select value={meetingFrequency} onValueChange={setMeetingFrequency}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="fortnightly">Fortnightly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Next Meeting */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Next Meeting</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full justify-start text-left font-normal',
                            !nextMeetingDate && 'text-muted-foreground'
                          )}
                        >
                          {nextMeetingDate ? nextMeetingDate.toLocaleDateString() : 'Pick a date (optional)'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={nextMeetingDate}
                          onSelect={setNextMeetingDate}
                          disabled={(d) => d < new Date()}
                          initialFocus
                          className={cn('p-3 pointer-events-auto')}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Location */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Location / Link</Label>
                    <Input
                      placeholder="e.g. Zoom link or café address"
                      value={meetingLocation}
                      onChange={(e) => setMeetingLocation(e.target.value)}
                    />
                  </div>

                  {/* Reading Pace */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Reading Pace</Label>
                    <div className="flex flex-wrap gap-2">
                      {['One chapter per week', 'Whole book per month', 'Self-paced'].map(pace => (
                        <Badge
                          key={pace}
                          variant={readingPace === pace ? 'default' : 'outline'}
                          className="cursor-pointer transition-colors"
                          onClick={() => setReadingPace(readingPace === pace ? '' : pace)}
                        >
                          {pace}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Genre Focus */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Genre Focus</Label>
                    <div className="flex flex-wrap gap-2">
                      {['Fiction', 'Non-Fiction', 'Sci-Fi', 'Fantasy', 'Mystery', 'Romance', 'Self-Help', 'Biography'].map(genre => (
                        <Badge
                          key={genre}
                          variant={genreFocus.includes(genre) ? 'default' : 'outline'}
                          className="cursor-pointer transition-colors"
                          onClick={() => setGenreFocus(prev =>
                            prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]
                          )}
                        >
                          {genre}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
            {/* Non-book-club group options */}
            {!isBookClubMode && (
              <>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="group-chat"
                    checked={isGroup}
                    onCheckedChange={(checked) => setIsGroup(checked === true)}
                  />
                  <Label htmlFor="group-chat">Create group chat</Label>
                </div>
                {isGroup && (
                  <>
                    <Input
                      placeholder="Group name (optional)"
                      value={groupTitle}
                      onChange={(e) => setGroupTitle(e.target.value)}
                    />
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="book-club"
                        checked={isBookClub}
                        onCheckedChange={(checked) => setIsBookClub(checked === true)}
                      />
                      <Label htmlFor="book-club" className="flex items-center gap-2">
                        <BookMarked className="w-4 h-4" />
                        Make this a Book Club
                      </Label>
                    </div>
                    {isBookClub && shareData?.kind === 'book' && (
                      <p className="text-xs text-muted-foreground">
                        📚 This book will be added to the club's reading list
                      </p>
                    )}
                  </>
                )}
              </>
            )}

            {/* Selected members summary */}
            {selectedUsers.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-sm font-medium text-muted-foreground">
                  {selectedUsers.length} member{selectedUsers.length !== 1 ? 's' : ''} selected
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedUsers.map((userId) => {
                    const u = followers.find(f => f.id === userId);
                    return (
                      <Badge key={userId} variant="secondary" className="flex items-center gap-1">
                        {u?.name || u?.username}
                        <X
                          className="h-3 w-3 cursor-pointer"
                          onClick={() => handleUserToggle(userId)}
                        />
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Member search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search followers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="max-h-48 overflow-y-auto space-y-1">
              {filteredFollowers.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No followers found
                </div>
              ) : (
                filteredFollowers.map((follower) => (
                  <div
                    key={follower.id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleUserToggle(follower.id)}
                  >
                    <Checkbox
                      checked={selectedUsers.includes(follower.id)}
                      onCheckedChange={() => handleUserToggle(follower.id)}
                    />
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={follower.image || undefined} />
                      <AvatarFallback>
                        {follower.name?.charAt(0) || follower.username.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{follower.name || follower.username}</p>
                      {follower.name && (
                        <p className="text-sm text-muted-foreground">@{follower.username}</p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="existing" className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredChats.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No chats found
                </div>
              ) : (
                filteredChats.map((chat) => (
                  <div
                    key={chat.id}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedChat === chat.id ? 'bg-primary/10' : 'hover:bg-muted/50'
                    }`}
                    onClick={() => setSelectedChat(chat.id)}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>
                          {chat.is_group ? <Users className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">
                          {chat.is_group ? (chat.title || 'Group Chat') : getOtherUserName(chat)}
                        </p>
                        {chat.is_group && (
                          <p className="text-sm text-muted-foreground">
                            {chat.members?.length || 0} members
                          </p>
                        )}
                      </div>
                    </div>
                    {selectedChat === chat.id && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleShare}
            disabled={!canShare() || loading}
          >
            {loading ? (
              <div className="h-4 w-4 animate-spin border-2 border-current border-t-transparent rounded-full mr-2" />
            ) : null}
            {mode === 'share' ? 'Share' : isBookClubMode ? 'Create Book Club' : 'Create Chat'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
